
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { Eraser, Loader2, Upload, SkipForward, AlertCircle, CheckCircle, Mic, Square, Play, Pause, Shield } from 'lucide-react'
import { getSupportedMimeType } from '../utils/audioToWav'
import Cookies from 'js-cookie'
import { API_BASE_URL } from '@/shared/services/api/api.config'
import { EnrollmentApi } from '@/features/enrollment/services/enrollment.service'

interface AgentConsentsStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    agentName?: string
    sessionId?: string
    isMasterMode?: boolean
    onComplete?: () => Promise<void> | void
    [key: string]: any
}

export default function AgentConsentsStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    agentName,
    sessionId,
    isMasterMode = false,
    revealAllFields = false,
    onComplete
}: AgentConsentsStepProps) {
    const signatureLabels = useMemo(() => ([
        { index: 1, field: 'drawableSignature1', label: 'Client Signature', description: 'Used for Intake + Fact Finder, CMS Authorization, and Income/Compliance PDFs' }
    ]), [])
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isUploading, setIsUploading] = useState(false)
    const [isUploadingSelfie, setIsUploadingSelfie] = useState(false)
    const [managerLoading, setManagerLoading] = useState(false)
    const fetchManagerAttempted = useRef(false)
    const [drawingPadIndex, setDrawingPadIndex] = useState<number | null>(null)
    const [signatureCompletion, setSignatureCompletion] = useState<Record<number, boolean>>({
        1: !!enrollmentData.drawableSignature1
    })
    const canvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({})
    const { user } = useAuth()

    // CMS Consent Recording State
    const [isCmsRecording, setIsCmsRecording] = useState(false)
    const [hasCmsRecorded, setHasCmsRecorded] = useState(!!enrollmentData.cmsVoiceConsentUrl)
    const [cmsTimer, setCmsTimer] = useState(0)
    const [cmsAudioUrl, setCmsAudioUrl] = useState<string | null>(null)
    const [isCmsPlaying, setIsCmsPlaying] = useState(false)
    const [cmsUploadProgress, setCmsUploadProgress] = useState<string>('')
    const [isCmsUploading, setIsCmsUploading] = useState(false)
    const cmsMediaRecorderRef = useRef<MediaRecorder | null>(null)
    const cmsAudioChunksRef = useRef<Blob[]>([])
    const cmsAudioRef = useRef<HTMLAudioElement | null>(null)

    // Carrier Consent Recording State
    const [isCarrierRecording, setIsCarrierRecording] = useState(false)
    const [hasCarrierRecorded, setHasCarrierRecorded] = useState(!!enrollmentData.carrierVoiceConsentUrl)
    const [carrierTimer, setCarrierTimer] = useState(0)
    const [carrierAudioUrl, setCarrierAudioUrl] = useState<string | null>(null)
    const [isCarrierPlaying, setIsCarrierPlaying] = useState(false)
    const [carrierUploadProgress, setCarrierUploadProgress] = useState<string>('')
    const [isCarrierUploading, setIsCarrierUploading] = useState(false)
    const carrierMediaRecorderRef = useRef<MediaRecorder | null>(null)
    const carrierAudioChunksRef = useRef<Blob[]>([])
    const carrierAudioRef = useRef<HTMLAudioElement | null>(null)

    const [recordingError, setRecordingError] = useState<string | null>(null)

    const clientFullName = `${enrollmentData.clientFirstName || ''} ${enrollmentData.clientLastName || ''}`.trim() || 'Client'
    const salesRepFullName = `${enrollmentData.referralAgentFirstName || ''} ${enrollmentData.referralAgentLastName || ''}`.trim() || 'Sales Rep'
    const customerIncome = enrollmentData.annualIncome || '0'

    // Static Licensed Agents for compliance
    const LICENSED_AGENT_1_NAME = "Anthony Ciponaer"
    const LICENSED_AGENT_2_NAME = "Ashley Harmer"

    // Helper function to format dates as "February 12 2026"
    const formatDateLong = (date: Date | string): string => {
        const d = typeof date === 'string' ? new Date(date) : date
        if (isNaN(d.getTime())) return '{Date}'
        return d.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })
    }

    // Format client DOB for display
    const formattedClientDOB = enrollmentData.clientDOB ? formatDateLong(enrollmentData.clientDOB) : '{Client Date of Birth}'
    const formattedToday = formatDateLong(new Date())

    // Auto-populate agent names from logged-in user
    useEffect(() => {
        if (user && user.firstName && user.lastName) {
            if (!enrollmentData.referralAgentFirstName || enrollmentData.referralAgentFirstName === 'undefined') {
                updateEnrollmentData('referralAgentFirstName', user.firstName)
            }
            if (!enrollmentData.referralAgentLastName || enrollmentData.referralAgentLastName === 'undefined') {
                updateEnrollmentData('referralAgentLastName', user.lastName)
            }
        } else if (agentName && agentName !== 'Unknown Agent' && !enrollmentData.referralAgentFirstName) {
            const parts = agentName.split(' ')
            const fName = parts[0] || ''
            const lName = parts.slice(1).join(' ') || ''
            updateEnrollmentData('referralAgentFirstName', fName)
            updateEnrollmentData('referralAgentLastName', lName)
        }

        // Clear any existing placeholder values
        if (enrollmentData.referralAgentNPN === 'TBD-MILESTONE-3') {
            updateEnrollmentData('referralAgentNPN', '')
        }
        if (enrollmentData.agent1Name === 'Licensed Agent 1 (TBD)') {
            updateEnrollmentData('agent1Name', '')
        }
        if (enrollmentData.agent2Name === 'Licensed Agent 2 (TBD)') {
            updateEnrollmentData('agent2Name', '')
        }
        if (enrollmentData.agent2NPN === 'TBD-MILESTONE-3') {
            updateEnrollmentData('agent2NPN', '')
        }
    }, [user, enrollmentData.referralAgentFirstName, enrollmentData.referralAgentLastName, enrollmentData.referralAgentNPN, enrollmentData.agent1Name, enrollmentData.agent2Name, enrollmentData.agent2NPN, updateEnrollmentData, agentName])

    useEffect(() => {
        signatureLabels.forEach(({ index, field }) => {
            const canvas = canvasRefs.current[index]
            if (!canvas) return

            canvas.width = canvas.offsetWidth
            canvas.height = canvas.offsetHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) return

            ctx.lineWidth = 2
            ctx.lineCap = 'round'
            ctx.strokeStyle = '#000000'
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            const signatureData = enrollmentData[field]
            if (signatureData) {
                const img = new window.Image()
                img.onload = () => {
                    ctx.clearRect(0, 0, canvas.width, canvas.height)
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
                }
                img.src = signatureData
            }
        })
    }, [enrollmentData, signatureLabels])

    // Auto-populate manager information from API
    useEffect(() => {
        const fetchManagerInfo = async () => {
            if (fetchManagerAttempted.current) return;
            fetchManagerAttempted.current = true;
            setManagerLoading(true);

            try {
                console.log('🔍 [AgentConsentsStep] Fetching manager info...');
                const managerInfo = await EnrollmentApi.getManagerInfo()
                console.log('🏢 [AgentConsentsStep] Manager info received:', managerInfo);

                if (managerInfo) {
                    if (managerInfo.managerName && (!enrollmentData.managerName || enrollmentData.managerName === '')) {
                        updateEnrollmentData('managerName', managerInfo.managerName)
                    }
                    if (managerInfo.managerEmail && (!enrollmentData.referralAgentEmail || enrollmentData.referralAgentEmail === '')) {
                        updateEnrollmentData('referralAgentEmail', managerInfo.managerEmail)
                    }
                }
            } catch (error) {
                console.error('❌ [AgentConsentsStep] Error fetching manager info:', error);
            } finally {
                setManagerLoading(false);
            }
        }

        // Only fetch if we don't already have values and haven't tried yet
        if (!fetchManagerAttempted.current && (!enrollmentData.managerName || !enrollmentData.referralAgentEmail)) {
            fetchManagerInfo()
        }
    }, [enrollmentData.managerName, enrollmentData.referralAgentEmail, updateEnrollmentData])

    // Auto-fill disqualification data as "No" (verified client is eligible)
    useEffect(() => {
        if (!enrollmentData.onMedicaidMedicare) {
            updateEnrollmentData('onMedicaidMedicare', 'No')
        }
        if (!enrollmentData.isHomeless) {
            updateEnrollmentData('isHomeless', 'No')
        }
        if (!enrollmentData.hasWorkInsurance) {
            updateEnrollmentData('hasWorkInsurance', 'No')
        }
    }, [])

    // Auto-select required consent checkboxes and attestations (Requirement J)
    useEffect(() => {
        const needsCmsConsent = enrollmentData.cmsWrittenConsent !== true;
        const needsCarrierConsent = enrollmentData.carrierWrittenConsent !== true;
        const needsIncomeAttestation = enrollmentData.incomeAttestation !== 'Yes';
        const needsNoGiftsAttestation = enrollmentData.noGiftsAttestation !== 'Yes';
        const needsMessageConsent = enrollmentData.messageConsent !== 'Yes';

        if (needsCmsConsent || needsCarrierConsent || needsIncomeAttestation || needsNoGiftsAttestation || needsMessageConsent) {
            // Batch updates if possible, or just update what's missing
            if (needsCmsConsent) {
                updateEnrollmentData('cmsWrittenConsent', true);
                if (!enrollmentData.cmsWrittenConsentAt) {
                    updateEnrollmentData('cmsWrittenConsentAt', new Date().toISOString());
                }
            }
            if (needsCarrierConsent) {
                updateEnrollmentData('carrierWrittenConsent', true);
                if (!enrollmentData.carrierWrittenConsentAt) {
                    updateEnrollmentData('carrierWrittenConsentAt', new Date().toISOString());
                }
            }
            if (needsIncomeAttestation) updateEnrollmentData('incomeAttestation', 'Yes');
            if (needsNoGiftsAttestation) updateEnrollmentData('noGiftsAttestation', 'Yes');
            if (needsMessageConsent) updateEnrollmentData('messageConsent', 'Yes');
        }
    }, [
        enrollmentData.cmsWrittenConsent,
        enrollmentData.carrierWrittenConsent,
        enrollmentData.incomeAttestation,
        enrollmentData.noGiftsAttestation,
        enrollmentData.messageConsent
    ])

    // Helper to get safe income string for attestation
    const displayIncome = useMemo(() => {
        const income = enrollmentData.annualIncome;
        if (income === undefined || income === null || income === '') return '0';
        return parseFloat(income.toString().replace(/[^0-9.]/g, '') || '0').toLocaleString();
    }, [enrollmentData.annualIncome]);

    // Get the session ID with multiple fallbacks
    const getSessionId = (): string => {
        const id = sessionId || enrollmentData.sessionId || localStorage.getItem('enrollmentSessionId') || ''
        return id
    }

    // Format time for recording display
    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Timer effects for CMS recording
    useEffect(() => {
        let interval: ReturnType<typeof setInterval>
        if (isCmsRecording) {
            interval = setInterval(() => {
                setCmsTimer(t => t + 1)
            }, 1000)
        }
        return () => clearInterval(interval)
    }, [isCmsRecording])

    // Timer effects for Carrier recording
    useEffect(() => {
        let interval: ReturnType<typeof setInterval>
        if (isCarrierRecording) {
            interval = setInterval(() => {
                setCarrierTimer(t => t + 1)
            }, 1000)
        }
        return () => clearInterval(interval)
    }, [isCarrierRecording])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cmsAudioRef.current) {
                cmsAudioRef.current.pause()
                cmsAudioRef.current = null
            }
            if (carrierAudioRef.current) {
                carrierAudioRef.current.pause()
                carrierAudioRef.current = null
            }
        }
    }, [])

    // Upload CMS voice consent to server
    const uploadCmsVoiceConsent = async (audioBlob: Blob, ext: string = 'webm'): Promise<string | null> => {
        try {
            setCmsUploadProgress('Uploading CMS voice consent...')
            const currentSessionId = getSessionId()
            if (!currentSessionId) {
                throw new Error('No session ID available')
            }

            const formData = new FormData()
            formData.append('voiceConsent', audioBlob, `cms-voice-consent-${Date.now()}.${ext}`)
            formData.append('sessionId', currentSessionId)

            const token = Cookies.get('token')
            const response = await fetch(`${API_BASE_URL}/enrollments/upload-cms-consent`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Upload failed' }))
                throw new Error(errorData.message || `Upload failed with status ${response.status}`)
            }

            const data = await response.json()
            setCmsUploadProgress('CMS voice consent uploaded successfully!')
            return data.voiceConsentUrl || null
        } catch (error) {
            console.error('Error uploading CMS voice consent:', error)
            setRecordingError(error instanceof Error ? error.message : 'Failed to upload CMS voice consent')
            return null
        }
    }

    // Upload Carrier voice consent to server
    const uploadCarrierVoiceConsent = async (audioBlob: Blob, ext: string = 'webm'): Promise<string | null> => {
        try {
            setCarrierUploadProgress('Uploading Carrier voice consent...')
            const currentSessionId = getSessionId()
            if (!currentSessionId) {
                throw new Error('No session ID available')
            }

            const formData = new FormData()
            formData.append('voiceConsent', audioBlob, `carrier-voice-consent-${Date.now()}.${ext}`)
            formData.append('sessionId', currentSessionId)

            const token = Cookies.get('token')
            const response = await fetch(`${API_BASE_URL}/enrollments/upload-carrier-consent`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Upload failed' }))
                throw new Error(errorData.message || `Upload failed with status ${response.status}`)
            }

            const data = await response.json()
            setCarrierUploadProgress('Carrier voice consent uploaded successfully!')
            return data.voiceConsentUrl || null
        } catch (error) {
            console.error('Error uploading Carrier voice consent:', error)
            setRecordingError(error instanceof Error ? error.message : 'Failed to upload Carrier voice consent')
            return null
        }
    }

    // Start CMS recording
    const startCmsRecording = async () => {
        try {
            setRecordingError(null)
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            const mimeType = getSupportedMimeType()
            const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
            cmsMediaRecorderRef.current = mediaRecorder
            cmsAudioChunksRef.current = []

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    cmsAudioChunksRef.current.push(event.data)
                }
            }

            mediaRecorder.onstop = async () => {
                // Use the recorder's actual mimeType — never hardcode audio/webm
                const recordedMimeType = cmsMediaRecorderRef.current?.mimeType || mimeType || 'audio/webm'
                const audioBlob = new Blob(cmsAudioChunksRef.current, { type: recordedMimeType })
                const url = URL.createObjectURL(audioBlob)
                setCmsAudioUrl(url)
                setHasCmsRecorded(true)
                stream.getTracks().forEach(track => track.stop())

                // Auto-upload after recording
                setIsCmsUploading(true)
                const ext = recordedMimeType.includes('mp4') ? 'mp4'
                    : recordedMimeType.includes('ogg') ? 'ogg'
                        : 'webm'
                const voiceUrl = await uploadCmsVoiceConsent(audioBlob, ext)
                if (voiceUrl) {
                    updateEnrollmentData('cmsVoiceConsentUrl', voiceUrl)
                    updateEnrollmentData('cmsVoiceConsentAt', new Date().toISOString())
                }
                setIsCmsUploading(false)
            }

            mediaRecorder.start()
            setIsCmsRecording(true)
            setCmsTimer(0)
        } catch (error) {
            console.error('Error accessing microphone:', error)
            setRecordingError('Microphone access denied. Please allow microphone access.')
        }
    }

    // Stop CMS recording
    const stopCmsRecording = () => {
        if (cmsMediaRecorderRef.current && isCmsRecording) {
            cmsMediaRecorderRef.current.stop()
            setIsCmsRecording(false)
        }
    }

    // Toggle CMS playback
    const toggleCmsPlayback = () => {
        if (!cmsAudioRef.current && cmsAudioUrl) {
            cmsAudioRef.current = new Audio(cmsAudioUrl)
            cmsAudioRef.current.onended = () => setIsCmsPlaying(false)
        }
        if (cmsAudioRef.current) {
            if (isCmsPlaying) {
                cmsAudioRef.current.pause()
                setIsCmsPlaying(false)
            } else {
                cmsAudioRef.current.play()
                setIsCmsPlaying(true)
            }
        }
    }

    // Re-record CMS
    const handleCmsReRecord = () => {
        if (cmsAudioRef.current) {
            cmsAudioRef.current.pause()
            cmsAudioRef.current = null
        }
        if (cmsAudioUrl) {
            URL.revokeObjectURL(cmsAudioUrl)
        }
        setCmsAudioUrl(null)
        setHasCmsRecorded(false)
        setIsCmsPlaying(false)
        setCmsUploadProgress('')
        updateEnrollmentData('cmsVoiceConsentUrl', null)
        updateEnrollmentData('cmsVoiceConsentAt', null)
    }

    // Start Carrier recording
    const startCarrierRecording = async () => {
        try {
            setRecordingError(null)
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            const mimeType = getSupportedMimeType()
            const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
            carrierMediaRecorderRef.current = mediaRecorder
            carrierAudioChunksRef.current = []

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    carrierAudioChunksRef.current.push(event.data)
                }
            }

            mediaRecorder.onstop = async () => {
                // Use the recorder's actual mimeType — never hardcode audio/webm
                const recordedMimeType = carrierMediaRecorderRef.current?.mimeType || mimeType || 'audio/webm'
                const audioBlob = new Blob(carrierAudioChunksRef.current, { type: recordedMimeType })
                const url = URL.createObjectURL(audioBlob)
                setCarrierAudioUrl(url)
                setHasCarrierRecorded(true)
                stream.getTracks().forEach(track => track.stop())

                // Auto-upload after recording
                setIsCarrierUploading(true)
                const ext = recordedMimeType.includes('mp4') ? 'mp4'
                    : recordedMimeType.includes('ogg') ? 'ogg'
                        : 'webm'
                const voiceUrl = await uploadCarrierVoiceConsent(audioBlob, ext)
                if (voiceUrl) {
                    updateEnrollmentData('carrierVoiceConsentUrl', voiceUrl)
                    updateEnrollmentData('carrierVoiceConsentAt', new Date().toISOString())
                }
                setIsCarrierUploading(false)
            }

            mediaRecorder.start()
            setIsCarrierRecording(true)
            setCarrierTimer(0)
        } catch (error) {
            console.error('Error accessing microphone:', error)
            setRecordingError('Microphone access denied. Please allow microphone access.')
        }
    }

    // Stop Carrier recording
    const stopCarrierRecording = () => {
        if (carrierMediaRecorderRef.current && isCarrierRecording) {
            carrierMediaRecorderRef.current.stop()
            setIsCarrierRecording(false)
        }
    }

    // Toggle Carrier playback
    const toggleCarrierPlayback = () => {
        if (!carrierAudioRef.current && carrierAudioUrl) {
            carrierAudioRef.current = new Audio(carrierAudioUrl)
            carrierAudioRef.current.onended = () => setIsCarrierPlaying(false)
        }
        if (carrierAudioRef.current) {
            if (isCarrierPlaying) {
                carrierAudioRef.current.pause()
                setIsCarrierPlaying(false)
            } else {
                carrierAudioRef.current.play()
                setIsCarrierPlaying(true)
            }
        }
    }

    // Re-record Carrier
    const handleCarrierReRecord = () => {
        if (carrierAudioRef.current) {
            carrierAudioRef.current.pause()
            carrierAudioRef.current = null
        }
        if (carrierAudioUrl) {
            URL.revokeObjectURL(carrierAudioUrl)
        }
        setCarrierAudioUrl(null)
        setHasCarrierRecorded(false)
        setIsCarrierPlaying(false)
        setCarrierUploadProgress('')
        updateEnrollmentData('carrierVoiceConsentUrl', null)
        updateEnrollmentData('carrierVoiceConsentAt', null)
    }

    const handleNext = useCallback(async (): Promise<boolean> => {
        const validationErrors: ValidationErrors = {}

        // Agent validation
        if (!enrollmentData.referralAgentFirstName) {
            validationErrors.referralAgentFirstName = 'Agent first name is required'
        }
        if (!enrollmentData.referralAgentLastName) {
            validationErrors.referralAgentLastName = 'Agent last name is required'
        }
        // managerName and referralAgentEmail are optional — auto-filled when available

        // CMS Consent validation
        if (!enrollmentData.cmsWrittenConsent) {
            validationErrors.cmsWrittenConsent = 'CMS written consent is required'
        }

        // Carrier Consent validation
        if (!enrollmentData.carrierWrittenConsent) {
            validationErrors.carrierWrittenConsent = 'Carrier written consent is required'
        }

        // CMS Voice Consent Recording validation (REQUIRED)
        if (!enrollmentData.cmsVoiceConsentUrl) {
            validationErrors.cmsVoiceConsent = 'CMS voice consent recording is required'
        }

        // Carrier Voice Consent Recording validation (REQUIRED)
        if (!enrollmentData.carrierVoiceConsentUrl) {
            validationErrors.carrierVoiceConsent = 'Carrier voice consent recording is required'
        }

        // Document validation (ID is required unless skipped)
        if (!enrollmentData.idDocument?.base64 && !enrollmentData.documentSkipped) {
            validationErrors.idDocument = 'ID Document is required'
        }

        // Signature validation
        const missingSignatures = signatureLabels.filter(({ index }) => !signatureCompletion[index])
        if (missingSignatures.length > 0) {
            validationErrors.signature = 'Client signature is required'
        }
        if (enrollmentData.incomeAttestation !== 'Yes') {
            validationErrors.incomeAttestation = 'Income attestation is required'
        }
        if (enrollmentData.noGiftsAttestation !== 'Yes') {
            validationErrors.noGiftsAttestation = 'Gift attestation is required'
        }
        if (enrollmentData.messageConsent !== 'Yes') {
            validationErrors.messageConsent = 'Message consent is required'
        }

        setErrors(validationErrors)
        return Object.keys(validationErrors).length === 0
    }, [enrollmentData, signatureCompletion, signatureLabels])

    // File upload handlers
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setErrors({ ...errors, idDocument: 'File size must be less than 10MB' })
                return
            }

            setIsUploading(true)
            try {
                const base64 = await fileToBase64(file)
                updateEnrollmentData('idDocument', {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                    base64: base64
                })
            } catch (error) {
                setErrors({ ...errors, idDocument: 'Failed to read file. Please try again.' })
            } finally {
                setIsUploading(false)
            }
        }
    }

    const handleSelfieChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setErrors({ ...errors, selfieDocument: 'File size must be less than 10MB' })
                return
            }

            setIsUploadingSelfie(true)
            try {
                const base64 = await fileToBase64(file)
                updateEnrollmentData('selfieDocument', {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                    base64: base64
                })
            } catch (error) {
                setErrors({ ...errors, selfieDocument: 'Failed to read file. Please try again.' })
            } finally {
                setIsUploadingSelfie(false)
            }
        }
    }

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = (error) => reject(error)
        })
    }

    const handleSkipDocument = () => {
        updateEnrollmentData('documentSkipped', true)
        updateEnrollmentData('idDocument', null)
    }

    // Signature handlers
    const getCoordinates = (event: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRefs.current[drawingPadIndex || 1]
        if (!canvas) return { x: 0, y: 0 }
        const rect = canvas.getBoundingClientRect()

        let clientX, clientY
        if ('touches' in event) {
            clientX = event.touches[0].clientX
            clientY = event.touches[0].clientY
        } else {
            clientX = (event as React.MouseEvent).clientX
            clientY = (event as React.MouseEvent).clientY
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        }
    }

    const startDrawing = (signatureIndex: number, event: React.MouseEvent | React.TouchEvent) => {
        setDrawingPadIndex(signatureIndex)
        const { x, y } = getCoordinates(event)
        const ctx = canvasRefs.current[signatureIndex]?.getContext('2d')
        if (ctx) {
            ctx.beginPath()
            ctx.moveTo(x, y)
        }
    }

    const draw = (event: React.MouseEvent | React.TouchEvent) => {
        if (drawingPadIndex === null) return
        event.preventDefault()
        const { x, y } = getCoordinates(event)
        const ctx = canvasRefs.current[drawingPadIndex]?.getContext('2d')
        if (ctx) {
            ctx.lineTo(x, y)
            ctx.stroke()
            setSignatureCompletion((prev) => ({ ...prev, [drawingPadIndex]: true }))
        }
    }

    const stopDrawing = (signatureIndex: number) => {
        setDrawingPadIndex(null)
        const canvas = canvasRefs.current[signatureIndex]
        if (canvas) {
            const dataUrl = canvas.toDataURL()
            updateEnrollmentData(`drawableSignature${signatureIndex}`, dataUrl)
            if (signatureIndex === 1) {
                updateEnrollmentData('clientSignature', dataUrl)
                updateEnrollmentData('drawableSignature2', dataUrl)
                updateEnrollmentData('drawableSignature3', dataUrl)
            }
            updateEnrollmentData('typedFullNameSignature', clientFullName)
            updateEnrollmentData('applicationDate', new Date().toISOString())
            setSignatureCompletion((prev) => ({ ...prev, [signatureIndex]: true }))
        }
    }

    const clearSignature = (signatureIndex: number) => {
        const canvas = canvasRefs.current[signatureIndex]
        const ctx = canvas?.getContext('2d')
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            updateEnrollmentData(`drawableSignature${signatureIndex}`, null)
            if (signatureIndex === 1) {
                updateEnrollmentData('clientSignature', null)
                updateEnrollmentData('drawableSignature2', null)
                updateEnrollmentData('drawableSignature3', null)
            }
            setSignatureCompletion((prev) => ({ ...prev, [signatureIndex]: false }))
        }
    }

    const hasIdFile = !!enrollmentData.idDocument?.base64
    const hasSelfieFile = !!enrollmentData.selfieDocument?.base64
    const isDisqualified = enrollmentData.onMedicaidMedicare === 'Yes' ||
        enrollmentData.isHomeless === 'Yes' ||
        enrollmentData.hasWorkInsurance === 'Yes'

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 3}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            onNext={handleNext}
            onComplete={onComplete}
            isNavigating={isNavigating}
            nextButtonText="Continue to Compliance"
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 4: Agent & Consents</h3>
                        <p className="text-sm text-accent-600">Agent details, consents, documents, and signature.</p>
                    </div>
                </div>



                <div className="space-y-8">
                    {/* SECTION: Agent Details - Master & Reveal All ONLY (Requirement: Hide from standard Agent View) */}
                    {(isMasterMode && revealAllFields) && (
                        <div className="mb-8">
                            <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Agent Details</h4>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">
                                            SALES REP FIRST NAME <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={enrollmentData.referralAgentFirstName || ''}
                                            onChange={(e) => updateEnrollmentData('referralAgentFirstName', e.target.value)}
                                            readOnly={!isMasterMode}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">
                                            SALES REP LAST NAME <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={enrollmentData.referralAgentLastName || ''}
                                            onChange={(e) => updateEnrollmentData('referralAgentLastName', e.target.value)}
                                            readOnly={!isMasterMode}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">Suffix</label>
                                        <input
                                            type="text"
                                            value={enrollmentData.agentSuffix || ''}
                                            onChange={(e) => updateEnrollmentData('agentSuffix', e.target.value)}
                                            readOnly={!isMasterMode}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">Manager's Name</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.managerName || ''}
                                        onChange={(e) => updateEnrollmentData('managerName', e.target.value)}
                                        readOnly={!isMasterMode}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'} ${errors.managerName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder={managerLoading ? "Loading manager info..." : "Enter Manager Name"}
                                    />
                                    {errors.managerName && <p className="mt-1 text-sm text-red-600">{errors.managerName}</p>}
                                    <p className="mt-1 text-xs text-gray-500">Auto-filled based on manager assigned to your group or agent</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">MANAGER EMAIL</label>
                                        <input
                                            type="email"
                                            value={enrollmentData.referralAgentEmail || ''}
                                            onChange={(e) => updateEnrollmentData('referralAgentEmail', e.target.value)}
                                            readOnly={!isMasterMode}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'} ${errors.referralAgentEmail ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder={managerLoading ? "Loading manager email..." : "Enter Manager Email"}
                                        />
                                        {errors.referralAgentEmail && <p className="mt-1 text-sm text-red-600">{errors.referralAgentEmail}</p>}
                                        <p className="mt-1 text-xs text-gray-500">Auto-filled based on manager's email for system alerts from JotForm (will be phased out)</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">Licensed Agent 1 Full Name</label>
                                        <input
                                            type="text"
                                            value={enrollmentData.agent1Name || ''}
                                            onChange={(e) => updateEnrollmentData('agent1Name', e.target.value)}
                                            readOnly={!isMasterMode}
                                            placeholder="Will be auto-filled from assigned licensed agents"
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">
                                            Agent 1 NPN
                                        </label>
                                        <input
                                            type="text"
                                            value={enrollmentData.referralAgentNPN || ''}
                                            onChange={(e) => updateEnrollmentData('referralAgentNPN', e.target.value)}
                                            readOnly={!isMasterMode}
                                            placeholder="Will be auto-filled from assigned licensed agents"
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">Licensed Agent 2 Full Name</label>
                                        <input
                                            type="text"
                                            value={enrollmentData.agent2Name || ''}
                                            onChange={(e) => updateEnrollmentData('agent2Name', e.target.value)}
                                            readOnly={!isMasterMode}
                                            placeholder="Will be auto-filled from assigned licensed agents"
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-tight">Agent 2 NPN</label>
                                        <input
                                            type="text"
                                            value={enrollmentData.agent2NPN || ''}
                                            onChange={(e) => updateEnrollmentData('agent2NPN', e.target.value)}
                                            readOnly={!isMasterMode}
                                            placeholder="Will be auto-filled from assigned licensed agents"
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isMasterMode ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}



                    {/* SECTION: CMS Voice Consent Recording */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center gap-2">
                            <Mic className="w-5 h-5 text-blue-600" />
                            CMS Voice Consent Recording <span className="text-red-500">*</span>
                        </h4>

                        {/* CMS Recording Script */}
                        <div className="bg-blue-50 p-4 rounded-md border border-blue-100 mb-4">
                            <h5 className="text-sm font-bold text-blue-800 uppercase mb-2">Read This Out Loud When Recording Consent:</h5>
                            <div className="space-y-3 text-blue-900 text-sm">
                                <p><strong>AGENT SAYS:</strong> "My name is <u>{enrollmentData.referralAgentFirstName || '{Sales REP First and Last Name}'} {enrollmentData.referralAgentLastName || ''}</u> I am with <u>{enrollmentData.clientFirstName || '{Client First and Last Name}'} {enrollmentData.clientLastName || ''}</u> on {formattedToday}. Please Say your Full Name and Date of Birth."</p>
                                <p><strong>CLIENT SAYS:</strong> "My name is <u>{enrollmentData.clientFirstName || '{Client Full Name}'} {enrollmentData.clientLastName || ''}</u> and my date of birth is <u>{formattedClientDOB}</u>."</p>
                                <p><strong>AGENT SAYS:</strong> "I give my permission to Health Connect Marketing and their licensed agents <span className="bg-yellow-200 px-1 font-semibold">Anthony Ciponaer</span> and <span className="bg-yellow-200 px-1 font-semibold">Ashley Harmer</span> CMS authorization to enroll me into a health insurance plan. I have read and understand the authorization and provided my consent to enroll into any selected plan. Do You Agree?"</p>
                            </div>
                        </div>

                        {/* Recording Error */}
                        {recordingError && (
                            <div className="bg-red-50 p-3 rounded-md border border-red-200 mb-4">
                                <p className="text-sm text-red-800">{recordingError}</p>
                            </div>
                        )}

                        {/* CMS Upload Progress */}
                        {isCmsUploading && (
                            <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200 mb-4 flex items-center gap-2">
                                <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />
                                <p className="text-sm font-medium text-yellow-800">{cmsUploadProgress}</p>
                            </div>
                        )}

                        {/* CMS Upload Success */}
                        {enrollmentData.cmsVoiceConsentUrl && (
                            <div className="bg-green-50 p-3 rounded-md border border-green-200 mb-4 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-800">CMS Voice Consent Uploaded ({new Date(enrollmentData.cmsVoiceConsentAt).toLocaleString()})</span>
                            </div>
                        )}

                        {/* CMS Recording Controls */}
                        <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50">
                            {!hasCmsRecorded && !isCmsRecording && (
                                <div className="text-center">
                                    <p className="mb-3 text-sm font-medium text-gray-600">Press to record CMS consent</p>
                                    <button
                                        onClick={startCmsRecording}
                                        disabled={isCmsUploading}
                                        className="h-14 w-14 rounded-full bg-red-600 flex items-center justify-center hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all shadow-lg disabled:opacity-50"
                                    >
                                        <Mic className="h-7 w-7 text-white" />
                                    </button>
                                </div>
                            )}

                            {isCmsRecording && (
                                <div className="flex flex-col items-center">
                                    <div className="mb-3 text-xl font-mono text-red-600 animate-pulse">
                                        {formatTime(cmsTimer)}
                                    </div>
                                    <button
                                        onClick={stopCmsRecording}
                                        className="h-14 w-14 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-all shadow-lg"
                                    >
                                        <Square className="h-6 w-6 text-white" />
                                    </button>
                                    <p className="mt-2 text-sm text-gray-500 animate-pulse">Recording...</p>
                                </div>
                            )}

                            {hasCmsRecorded && !isCmsRecording && cmsAudioUrl && (
                                <div className="flex flex-col items-center w-full">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                                        <CheckCircle className="h-6 w-6 text-green-600" />
                                    </div>
                                    <p className="text-sm font-medium text-green-800 mb-3">CMS Consent Recorded</p>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={toggleCmsPlayback}
                                            disabled={isCmsUploading}
                                            className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-700 transition-all disabled:opacity-50"
                                        >
                                            {isCmsPlaying ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white ml-0.5" />}
                                        </button>
                                        <button
                                            onClick={handleCmsReRecord}
                                            disabled={isCmsUploading}
                                            className="text-sm text-blue-600 hover:text-blue-800 underline font-medium disabled:opacity-50"
                                        >
                                            Re-record
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {errors.cmsVoiceConsent && <p className="mt-2 text-sm text-red-600 text-center">{errors.cmsVoiceConsent}</p>}

                        {/* CMS Written Consent Checkbox */}
                        <div className="mt-4 p-4 border border-blue-200 rounded-lg bg-blue-50/30">
                            <div className="flex items-start">
                                <div className="flex h-5 items-center">
                                    <input
                                        id="cmsWrittenConsent"
                                        type="checkbox"
                                        checked={enrollmentData.cmsWrittenConsent === true}
                                        onChange={(e) => {
                                            updateEnrollmentData('cmsWrittenConsent', e.target.checked)
                                            if (e.target.checked) {
                                                updateEnrollmentData('cmsWrittenConsentAt', new Date().toISOString())
                                            } else {
                                                updateEnrollmentData('cmsWrittenConsentAt', null)
                                            }
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label htmlFor="cmsWrittenConsent" className="font-medium text-gray-900">CMS Written Consent *</label>
                                    <p className="text-gray-600 text-xs mt-1 italic">
                                        "I {enrollmentData.clientFirstName || '{Customer Name}'} {enrollmentData.clientLastName || ''} give approval to Health Connect Marketing and its licensed agents Anthony Ciponaer and Ashley Harmer to be the Authorized User &amp; Authorized Agent to speak on the my behalf for 365 Days."
                                    </p>
                                </div>
                            </div>
                            {errors.cmsWrittenConsent && <p className="mt-1 text-sm text-red-600">{errors.cmsWrittenConsent}</p>}
                        </div>
                    </div>

                    {/* SECTION: Carrier Voice Consent Recording */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center gap-2">
                            <Mic className="w-5 h-5 text-orange-600" />
                            Carrier Voice Consent Recording <span className="text-red-500">*</span>
                        </h4>

                        {/* Carrier Recording Script */}
                        <div className="bg-orange-50 p-5 rounded-lg border-2 border-orange-200 mb-4">
                            <h5 className="text-sm font-bold text-orange-800 uppercase mb-3">Have The Client Read The Following:</h5>
                            <div className="bg-white p-5 rounded-lg border-2 border-orange-300 shadow-sm">
                                <p className="text-orange-800 font-bold text-base mb-3">CLIENT SAYS:</p>
                                <p className="text-gray-900 text-lg leading-relaxed italic">
                                    "My name is {enrollmentData.clientFirstName || '{Customer Name}'} {enrollmentData.clientLastName || ''}, and my date of birth is{' '}
                                    <span className="font-bold underline decoration-2">{formattedClientDOB}</span>.
                                    I give permission to Health Connect Marketing and my agents,{' '}
                                    <span className="bg-yellow-200 px-1 font-semibold not-italic">{LICENSED_AGENT_1_NAME}</span>
                                    {' '}and{' '}
                                    <span className="bg-yellow-200 px-1 font-semibold not-italic">{LICENSED_AGENT_2_NAME}</span>
                                    , to enroll me in an {enrollmentData.carrierDesired || '{Insurance carrier selected}'} health insurance plan for the 2026 plan year, and to assist with the renewal of my {enrollmentData.carrierDesired || '{Carrier}'} plan for 2027, unless I revoke this authorization in writing."
                                </p>
                            </div>
                        </div>

                        {/* Carrier Upload Progress */}
                        {isCarrierUploading && (
                            <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200 mb-4 flex items-center gap-2">
                                <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />
                                <p className="text-sm font-medium text-yellow-800">{carrierUploadProgress}</p>
                            </div>
                        )}

                        {/* Carrier Upload Success */}
                        {enrollmentData.carrierVoiceConsentUrl && (
                            <div className="bg-green-50 p-3 rounded-md border border-green-200 mb-4 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-800">Carrier Voice Consent Uploaded ({new Date(enrollmentData.carrierVoiceConsentAt).toLocaleString()})</span>
                            </div>
                        )}

                        {/* Carrier Recording Controls */}
                        <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-orange-300 rounded-lg bg-orange-50/50">
                            {!hasCarrierRecorded && !isCarrierRecording && (
                                <div className="text-center">
                                    <p className="mb-3 text-sm font-medium text-gray-600">Press to record Carrier consent</p>
                                    <button
                                        onClick={startCarrierRecording}
                                        disabled={isCarrierUploading}
                                        className="h-14 w-14 rounded-full bg-orange-600 flex items-center justify-center hover:bg-orange-700 focus:outline-none focus:ring-4 focus:ring-orange-300 transition-all shadow-lg disabled:opacity-50"
                                    >
                                        <Mic className="h-7 w-7 text-white" />
                                    </button>
                                </div>
                            )}

                            {isCarrierRecording && (
                                <div className="flex flex-col items-center">
                                    <div className="mb-3 text-xl font-mono text-orange-600 animate-pulse">
                                        {formatTime(carrierTimer)}
                                    </div>
                                    <button
                                        onClick={stopCarrierRecording}
                                        className="h-14 w-14 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-all shadow-lg"
                                    >
                                        <Square className="h-6 w-6 text-white" />
                                    </button>
                                    <p className="mt-2 text-sm text-gray-500 animate-pulse">Recording...</p>
                                </div>
                            )}

                            {hasCarrierRecorded && !isCarrierRecording && carrierAudioUrl && (
                                <div className="flex flex-col items-center w-full">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                                        <CheckCircle className="h-6 w-6 text-green-600" />
                                    </div>
                                    <p className="text-sm font-medium text-green-800 mb-3">Carrier Consent Recorded</p>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={toggleCarrierPlayback}
                                            disabled={isCarrierUploading}
                                            className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center hover:bg-orange-700 transition-all disabled:opacity-50"
                                        >
                                            {isCarrierPlaying ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white ml-0.5" />}
                                        </button>
                                        <button
                                            onClick={handleCarrierReRecord}
                                            disabled={isCarrierUploading}
                                            className="text-sm text-orange-600 hover:text-orange-800 underline font-medium disabled:opacity-50"
                                        >
                                            Re-record
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {errors.carrierVoiceConsent && <p className="mt-2 text-sm text-red-600">{errors.carrierVoiceConsent}</p>}

                        {/* Carrier Written Consent Checkbox */}
                        <div className="mt-4 p-4 border border-orange-200 rounded-lg bg-orange-50/30">
                            <div className="flex items-start">
                                <div className="flex h-5 items-center">
                                    <input
                                        id="carrierWrittenConsent"
                                        type="checkbox"
                                        checked={enrollmentData.carrierWrittenConsent === true}
                                        onChange={(e) => {
                                            updateEnrollmentData('carrierWrittenConsent', e.target.checked)
                                            if (e.target.checked) {
                                                updateEnrollmentData('carrierWrittenConsentAt', new Date().toISOString())
                                            } else {
                                                updateEnrollmentData('carrierWrittenConsentAt', null)
                                            }
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label htmlFor="carrierWrittenConsent" className="font-medium text-gray-900">Carrier Written Consent *</label>
                                    <p className="text-gray-600 text-xs mt-1 italic">
                                        "I {enrollmentData.clientFirstName || '{Customer Name}'} {enrollmentData.clientLastName || ''} give approval to {LICENSED_AGENT_1_NAME} and {LICENSED_AGENT_2_NAME} to enroll me into a {enrollmentData.carrierDesired || '{Insurance carrier selected}'} health insurance plan for the year of 2026."
                                    </p>
                                </div>
                            </div>
                            {errors.carrierWrittenConsent && <p className="mt-1 text-sm text-red-600">{errors.carrierWrittenConsent}</p>}
                        </div>
                    </div>

                    {/* ID DOCUMENT UPLOAD */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center justify-between">
                            <span>ID DOCUMENT UPLOAD <span className="text-red-500">*</span></span>
                            <span className="text-xs font-medium px-2 py-0.5 bg-red-100 text-red-600 rounded">Required</span>
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Must be a Driver's License, Passport, or Signed Social Security Card (not expired)
                        </p>

                        {isUploading ? (
                            <div className="border-2 border-dashed border-indigo-300 rounded-lg p-8 text-center bg-indigo-50">
                                <Loader2 className="mx-auto h-10 w-10 text-indigo-500 animate-spin" />
                                <p className="mt-2 text-sm text-indigo-600">Processing file...</p>
                            </div>
                        ) : hasIdFile ? (
                            <div className="bg-green-50 p-4 rounded-md flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                    <span className="text-sm font-medium text-green-800">{enrollmentData.idDocument.name}</span>
                                    <span className="text-xs text-green-600">({(enrollmentData.idDocument.size / 1024).toFixed(1)} KB)</span>
                                </div>
                                <button
                                    onClick={() => updateEnrollmentData('idDocument', null)}
                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                >
                                    Remove
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors">
                                    <Upload className="mx-auto h-10 w-10 text-gray-400" />
                                    <div className="mt-2 text-sm text-gray-600">
                                        <label htmlFor="id-file-upload" className="relative cursor-pointer font-medium text-indigo-600 hover:text-indigo-500">
                                            <span>Upload a file</span>
                                            <input id="id-file-upload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*,.pdf" />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, PDF up to 10MB</p>
                                </div>
                            </div>
                        )}
                        {errors.idDocument && <p className="mt-2 text-sm text-red-600 font-medium">{errors.idDocument}</p>}
                    </div>

                    {/* SELFIE OF CLIENT UPLOAD */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b flex items-center justify-between">
                            <span>SELFIE OF CLIENT UPLOAD</span>
                            <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Optional</span>
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Optional: Upload a photo of the client holding their ID for additional verification.
                        </p>

                        {isUploadingSelfie ? (
                            <div className="border-2 border-dashed border-indigo-300 rounded-lg p-8 text-center bg-indigo-50">
                                <Loader2 className="mx-auto h-10 w-10 text-indigo-500 animate-spin" />
                                <p className="mt-2 text-sm text-indigo-600">Processing file...</p>
                            </div>
                        ) : hasSelfieFile ? (
                            <div className="bg-green-50 p-4 rounded-md flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                    <span className="text-sm font-medium text-green-800">{enrollmentData.selfieDocument.name}</span>
                                    <span className="text-xs text-green-600">({(enrollmentData.selfieDocument.size / 1024).toFixed(1)} KB)</span>
                                </div>
                                <button
                                    onClick={() => updateEnrollmentData('selfieDocument', null)}
                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                >
                                    Remove
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors">
                                    <Upload className="mx-auto h-10 w-10 text-gray-400" />
                                    <div className="mt-2 text-sm text-gray-600">
                                        <label htmlFor="selfie-file-upload" className="relative cursor-pointer font-medium text-indigo-600 hover:text-indigo-500">
                                            <span>Upload a file</span>
                                            <input id="selfie-file-upload" type="file" className="sr-only" onChange={handleSelfieChange} accept="image/*,.pdf" />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, PDF up to 10MB</p>
                                </div>
                            </div>
                        )}
                        {errors.selfieDocument && <p className="mt-2 text-sm text-red-600 font-medium">{errors.selfieDocument}</p>}
                    </div>



                    {/* SECTION: Signature & Attestations */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Signature &amp; Attestations</h4>
                        <p className="text-sm text-red-600 font-medium mb-4">CLIENT MUST SIGN THIS. AGENT IS NOT ALLOWED TO SIGN.</p>

                        <div className="space-y-4">

                            {/* Income Attestation */}
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-900 mb-3 font-medium">
                                    I <b>{clientFullName}</b> attest that my income of <b>${displayIncome}</b> is accurate.
                                </p>
                                <div className="flex items-center space-x-4">
                                    <label className="flex items-center">
                                        <input type="radio" name="incomeAttestation" value="Yes" checked={enrollmentData.incomeAttestation === 'Yes'} onChange={() => updateEnrollmentData('incomeAttestation', 'Yes')} className="h-4 w-4 text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name="incomeAttestation" value="No" checked={enrollmentData.incomeAttestation === 'No'} onChange={() => updateEnrollmentData('incomeAttestation', 'No')} className="h-4 w-4 text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-700">No</span>
                                    </label>
                                </div>
                                {errors.incomeAttestation && <p className="mt-1 text-sm text-red-600">{errors.incomeAttestation}</p>}
                            </div>

                            {/* Gifts Attestation */}
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-900 mb-3 font-medium">
                                    I <b>{clientFullName}</b> attest that I have not received any gifts, incentives, or cash from <b>{LICENSED_AGENT_1_NAME}</b>, <b>{LICENSED_AGENT_2_NAME}</b>, or Health Connect Marketing.
                                </p>
                                <div className="flex items-center space-x-4">
                                    <label className="flex items-center">
                                        <input type="radio" name="noGiftsAttestation" value="Yes" checked={enrollmentData.noGiftsAttestation === 'Yes'} onChange={() => updateEnrollmentData('noGiftsAttestation', 'Yes')} className="h-4 w-4 text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name="noGiftsAttestation" value="No" checked={enrollmentData.noGiftsAttestation === 'No'} onChange={() => updateEnrollmentData('noGiftsAttestation', 'No')} className="h-4 w-4 text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-700">No</span>
                                    </label>
                                </div>
                                {errors.noGiftsAttestation && <p className="mt-1 text-sm text-red-600">{errors.noGiftsAttestation}</p>}
                            </div>

                            {/* Message Consent */}
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-start">
                                    <input
                                        id="messageConsent"
                                        type="checkbox"
                                        checked={enrollmentData.messageConsent === 'Yes'}
                                        onChange={(e) => updateEnrollmentData('messageConsent', e.target.checked ? 'Yes' : 'No')}
                                        className="h-4 w-4 mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="messageConsent" className="font-medium text-gray-700">Consent to receive messages? *</label>
                                        <p className="text-gray-500 text-xs mt-1">
                                            By checking, I authorize Health Connect Marketing LLC to contact me for enrollment and follow-up purposes.
                                        </p>
                                    </div>
                                </div>
                                {errors.messageConsent && <p className="mt-1 text-sm text-red-600">{errors.messageConsent}</p>}
                            </div>

                            {/* Terms and Acknowledgements */}
                            <div className="space-y-4 pt-4 border-t">
                                <h4 className="text-md font-semibold text-accent-800 mb-2">Terms and Acknowledgements</h4>
                                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 max-h-60 overflow-y-auto text-sm text-gray-700 space-y-4 shadow-inner">
                                    <div>
                                        <h5 className="font-bold underline mb-2">Acknowledgements - Patient Protection & Affordable Care Act (PPACA) Assistance</h5>
                                        <p className="font-semibold mb-2">Health Connect Marketing MI</p>
                                        <p className="mb-2">
                                            I (<b>{clientFullName}</b>) acknowledge that I have engaged the assistance of independent Health Insurance Agents (<b>{LICENSED_AGENT_1_NAME}</b> and <b>{LICENSED_AGENT_2_NAME}</b>) who are licensed and certified by the Centers for Medicare & Medicaid Services (CMS), to assist me in the enrollment process for the Affordable Care Act (ACA) through the Healthcare.gov (Marketplace) website.
                                        </p>
                                        <p className="mb-2">
                                            In doing so, I understand and agree that all information supplied by me is accurate to the best of my knowledge and voluntarily provided. If proof of income or any other information is required by the Marketplace, it is my sole responsibility to provide it. I understand that any changes, including but not limited to my information, income, or health plan, made by me can affect the eligibility/status of my health insurance plan. I also understand that I should be reporting my accurate income estimate, not the estimate that maximizes the amount of premium tax credit for which I may be eligible when completing or updating an application for eligibility.
                                        </p>
                                        <p className="mb-2">
                                            I understand that my current income and/or premium-cost limitations may result in enrollment in a plan with lower benefits. I understand that my Agent can contact me via text or email which I provided unless I have checked this box.
                                        </p>
                                        <p className="mb-2">
                                            By signing this Acknowledgement, I provide my signature, expressly authorizing Health Connect Marketing (Health Connect), their agents, or partner companies to contact me at the number and address provided with insurance quotes or to obtain additional information for such purpose, via live/prerecorded/autodialed calls, text messages, and/or emails solely for the purposes of enrollment, account maintenance, or policy renewal, until I revoke this consent in writing. I understand that my signature is not a condition of purchasing any property, goods, or services. I also understand that I can contact my Agents for any questions pertaining to my policy and/or any changes that I would like to discuss. I consent to my Agents calling the Marketplace Call Center to inquire about the status of a Marketplace enrollment or policy update as needed. If I need help or have questions about my policy, I understand that I can contact Health Connect Marketing and/or the Marketplace with the contact information below:
                                        </p>
                                        <div className="mb-2 font-semibold bg-white p-3 border border-gray-200 rounded-md">
                                            <p className="text-accent-900 border-b pb-1 mb-1">Health Connect Marketing</p>
                                            <p>Phone: (586) 328-3979</p>
                                            <p>Email: renewals@healthconnectmarketing.com</p>
                                        </div>
                                        <div className="mb-2 font-semibold bg-white p-3 border border-gray-200 rounded-md">
                                            <p className="text-accent-900 border-b pb-1 mb-1">Marketplace</p>
                                            <p>Phone: (800) 318-2596</p>
                                        </div>
                                        <p className="mb-2">
                                            If my policy requires a premium payment, I understand that payment must be made directly to the insurance carrier. My Agents may assist me in navigating the carrier’s payment system, but my Agents do not collect or process premium payments.
                                        </p>
                                        <p className="mb-2">
                                            I understand that my policy can be through, but is not limited to, Ambetter from Meridian, Aetna, Molina, United Health Care, Florida Blue, Blue Cross Blue Shield, Blue Care Network, Wellpoint, Oscar, Community First, Imperial, Cigna, and may or may not contain dental coverage. If I am unsure of what coverages are contained within my policy, I can read through my policy documents once they are mailed to me and/or reach out to Health Connect Marketing using the contact information above.
                                        </p>
                                        <p className="mb-2">
                                            The Customer acknowledges receipt of HIPAA Privacy Information at time of enrollment and agrees to allow Agent access to Healthcare.gov as needed to secure and maintain my health insurance policy throughout the initial term of the plan and subsequent renewals.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Permission To Review</h5>
                                        <p className="mb-2">
                                            I am voluntarily authorizing and giving consent to (<b>{LICENSED_AGENT_1_NAME}</b> and <b>{LICENSED_AGENT_2_NAME}</b>), as Licensed Health Insurance Agents, to review my health insurance enrollment now and annually, and advise me using all reasonable efforts, that I have made a sound choice for the current policy term and any subsequent policy terms. I understand the main roles of my Agents are to advise me on how I can become eligible for certain plans on the Marketplace, develop an understanding of my needs as a client, answer any questions I may have about my plan, and assist me in securing coverage.
                                        </p>
                                        <p className="mb-2">
                                            I understand that my Agents have permission to conduct a search for the consumer application using approved Classic Direct Enrollment/Enhanced Direct Enrollment websites in the Marketplace, assist with completing or updating an eligibility application, assist with plan selection and enrollment, and assist with ongoing account maintenance and application updates as required by the Marketplace.
                                        </p>
                                        <p className="mb-2">
                                            This authorization remains in effect for the duration of my Marketplace coverage unless revoked by me in writing. I acknowledge the names of my Agents. My signature below also confirms my understanding of all the above.
                                        </p>
                                        <p className="mb-2 font-semibold italic text-blue-800">
                                            I give my consent so you can assist me in obtaining Marketplace coverage. This includes my consent for you to search the Marketplace for applications, obtain quotes, apply for a subsidy, and apply for a current application.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Marketplace Consent</h5>
                                        <p className="mb-2">
                                            I give my permission to Health Connect Marketing to serve as the health insurance agent or broker for myself and my entire household if applicable, for purposes of enrollment in a Qualified Health Plan offered on the Federally Facilitated Marketplace. By consenting to this agreement, I authorize the above-mentioned Agent to view and use the confidential information provided by me in writing, electronically, or by telephone only for the purposes of one or more of the following:
                                        </p>
                                        <ul className="list-disc ml-5 mb-2 space-y-1">
                                            <li>Searching for an existing Marketplace application;</li>
                                            <li>Completing or updating an application for eligibility and enrollment in a Marketplace Qualified Health Plan or other government insurance affordability programs, such as Medicaid and CHIP, or advance tax credits to help pay for Marketplace premiums;</li>
                                            <li>Providing ongoing account maintenance and enrollment assistance, as necessary;</li>
                                            <li>Responding to inquiries from the Marketplace regarding my Marketplace application.</li>
                                        </ul>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Application Review Confirmation</h5>
                                        <p className="mb-2">
                                            I confirm that I reviewed the information entered on my Marketplace application prior to submission and verified that it accurately reflects the information I provided, voluntarily and to the best of my knowledge. I understand that my licensed Agent is assisting me with the application process, but the information submitted is based on my responses.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Marketplace and Direct Enrollment Authorization</h5>
                                        <p className="mb-2">
                                            I authorize Health Connect Marketing and its licensed Agents to assist me with accessing, completing, and submitting my health insurance application through the Federally Facilitated Marketplace and approved Direct Enrollment platforms such as HealthSherpa. This authorization includes assisting with plan selection, application submission, and account maintenance as necessary for enrollment and ongoing policy servicing. This authorization is limited to enrollment and account maintenance purposes only and does not authorize use of my information for marketing, sales, or unrelated purposes. I understand that my Agent is assisting me with the application process based on the information I provide, and that I may revoke this authorization at any time by notifying Health Connect Marketing in writing.
                                        </p>
                                        <p className="mb-2 font-semibold border-l-4 border-blue-400 pl-3">
                                            I understand that the Agent will not use or share my personally identifiable information (PII) for any purposes other than those explicitly listed above and will follow all applicable federal and state privacy laws, including HIPAA and CMS requirements.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Electronic Communication Consent</h5>
                                        <p className="mb-2">
                                            I consent to receiving text messages and emails from Health Connect Marketing and its licensed agents solely related to my ACA enrollment, plan management, and Marketplace communications, in accordance with federal electronic communication regulations (TCPA). I understand that I may revoke this consent at any time in writing.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">HIPAA Notice Acknowledgment</h5>
                                        <p className="mb-2">
                                            I acknowledge receipt of Health Connect Marketing’s HIPAA Notice of Privacy Practices at the time of enrollment, and I understand that my personal health information will be protected in accordance with HIPAA requirements.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Agent Platform Permissions</h5>
                                        <p className="mb-2">
                                            I authorize Health Connect Marketing and its licensed agents to view, update, or submit my application on approved Direct Enrollment platforms, such as HealthSherpa, solely for purposes of ACA enrollment, plan selection, application submission, account maintenance, and compliance with Marketplace requirements.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Revocation Options</h5>
                                        <p className="mb-2">
                                            I may revoke any consent provided in this document at any time by mail, email, or telephone, using the contact information provided in the main consent agreement. Revocation will be effective upon receipt by Health Connect Marketing.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Record Retention</h5>
                                        <p className="mb-2">
                                            Health Connect Marketing will retain records of my consent and application assistance for at least 10 years or as otherwise required by law, in order to maintain compliance with federal and state regulations.
                                        </p>
                                    </div>

                                    <div>
                                        <h5 className="font-bold underline mb-2">Subsidy Assistance Clarification</h5>
                                        <p className="mb-2">
                                            I understand that my Agent may assist with Marketplace subsidy applications only as it relates to my health insurance plan enrollment, and will not provide financial or tax advice outside the scope of ACA plan enrollment.
                                        </p>
                                        <p className="mb-2 font-semibold italic">
                                            I confirm that the information I provide for entry on my Marketplace eligibility and enrollment application will be true to the best of my knowledge. I understand that I do not have to share additional personal information about myself or my health with my Agent beyond what is required on the application for eligibility and enrollment purposes. I understand that my consent remains in effect until I revoke it, and I may revoke or modify my consent at any time by contacting Health Connect Marketing by telephone (586) 328-3979, email renewals@healthconnectmarketing.com, or mail to:
                                        </p>
                                        <div className="mb-2 font-bold italic border-l-4 border-blue-500 pl-4 bg-white p-3 rounded-md">
                                            ATTN Health Connect Marketing LLC<br />
                                            30 N Gould St Ste R,<br />
                                            Sheridan, WY, 82801
                                        </div>
                                        <p className="mb-2">
                                            I understand that revocation applies to all enrollment assistance and communications provided by my Agent.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Signature Canvases */}
                            <div className="pt-4 border-t">
                                <h5 className="text-md font-semibold text-accent-800 mb-2">Client Signatures</h5>
                                <div className="space-y-4 w-full">
                                    {signatureLabels.map((signature) => (
                                        <div key={signature.index} className="border border-gray-300 rounded-md p-4 bg-white w-full">
                                            <div className="mb-2 flex justify-between items-start gap-3 text-sm text-gray-500">
                                                <div>
                                                    <div className="font-medium text-gray-800">{signature.label}</div>
                                                    <div className="text-xs">{signature.description}</div>
                                                </div>
                                                <button onClick={() => clearSignature(signature.index)} className="flex items-center text-gray-600 hover:text-red-600">
                                                    <Eraser className="w-4 h-4 mr-1" /> Clear
                                                </button>
                                            </div>
                                            <div className="bg-white border border-gray-400 rounded cursor-crosshair h-40 w-full touch-none">
                                                <canvas
                                                    ref={(node) => {
                                                        canvasRefs.current[signature.index] = node
                                                    }}
                                                    className="w-full h-full"
                                                    onMouseDown={(event) => startDrawing(signature.index, event)}
                                                    onMouseMove={draw}
                                                    onMouseUp={() => stopDrawing(signature.index)}
                                                    onMouseLeave={() => stopDrawing(signature.index)}
                                                    onTouchStart={(event) => startDrawing(signature.index, event)}
                                                    onTouchMove={draw}
                                                    onTouchEnd={() => stopDrawing(signature.index)}
                                                />
                                            </div>
                                            <div className="mt-2 text-xs text-gray-500">
                                                Status: <span className={signatureCompletion[signature.index] ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                                    {signatureCompletion[signature.index] ? 'Captured' : 'Missing'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {errors.signature && <p className="mt-1 text-sm text-red-600">{errors.signature}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400 space-y-1">
                    <div>Application Date: <span className="text-gray-600 font-semibold">{enrollmentData.applicationDate ? new Date(enrollmentData.applicationDate).toLocaleDateString('en-US') : new Date().toLocaleDateString('en-US')}</span></div>
                    <div>IP Address: <span className="text-gray-600 font-semibold">{enrollmentData.ipAddress || enrollmentData.clientIp || 'Awaiting Capture...'}</span></div>
                </div>
            </div>
        </StageWrapper>
    )
}
